// SkipDashboardPage — /skip/dashboard.
//
// Operations dashboard for the Skip Management module. Mirrors the legacy
// `tricivo_system/operations/skips/dashboard.php` layout (KPI tiles +
// recent jobs table + recent enquiries table) but uses the new server
// endpoints from §7.1 / §7.2 / §7.5 / §7.6.

import { Link } from 'react-router-dom';
import { Container, Inbox, ListChecks, Truck } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { KpiCard } from '../../collections/dashboard/components/KpiCard';
import { useSkipEnquiries } from '../hooks/useSkipEnquiries';
import { useSkipInventory } from '../hooks/useSkipInventory';
import { useSkipJobs } from '../hooks/useSkipJobs';
import type { SkipEnquirySummary, SkipJobSummary } from '../../../types/api';
import { SkipJobStatusBadge } from '../jobs/components/SkipJobStatusBadge';
import { SkipEnquiryStatusBadge } from '../enquiries/components/SkipEnquiryStatusBadge';
import styles from './SkipDashboardPage.module.css';

const RECENT_LIMIT = 5;

function startOfMonthISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

export function SkipDashboardPage(): JSX.Element {
  // Active jobs — anything not completed/cancelled. Server doesn't have a
  // multi-status filter so we pull a generous page and trust the
  // pagination total when filtering by status. For the KPI we fetch the
  // delivered subset (the most operationally relevant active state) and
  // sum it with new + in_progress + awaiting_collection separately.
  // For simplicity, single page with limit 100 + client-side filter on
  // status for the KPI count.
  const allJobs = useSkipJobs({ page: 1, limit: 100 });
  const recentJobs = useSkipJobs({ page: 1, limit: RECENT_LIMIT });

  const openEnquiries = useSkipEnquiries({ page: 1, limit: 1, status: 'open' });
  const contactedEnquiries = useSkipEnquiries({ page: 1, limit: 1, status: 'contacted' });
  const recentEnquiries = useSkipEnquiries({ page: 1, limit: RECENT_LIMIT });

  const availableInventory = useSkipInventory({ page: 1, limit: 1, status: 'available' });

  const monthStart = startOfMonthISO();
  const today = todayISO();
  const jobsThisMonth = useSkipJobs({
    page: 1,
    limit: 1,
    date_from: monthStart,
    date_to: today,
  });

  // Derive "active jobs" from the first page total minus terminal-status rows.
  const activeJobsCount = (() => {
    const rows = allJobs.data?.jobs ?? [];
    return rows.filter((j) => j.status !== 'completed' && j.status !== 'cancelled').length;
  })();

  const isLoading =
    allJobs.isLoading ||
    openEnquiries.isLoading ||
    availableInventory.isLoading ||
    jobsThisMonth.isLoading;

  const openEnquiryTotal =
    (openEnquiries.data?.pagination.total ?? 0) +
    (contactedEnquiries.data?.pagination.total ?? 0);

  return (
    <div className={styles.page}>
      <PageHeader
        title="Skip Management Dashboard"
        subtitle="Monitor and manage all skip/bin/roro jobs"
      />

      <div className={styles.kpiGrid}>
        <KpiCard
          label="Active Jobs"
          value={activeJobsCount}
          icon={Truck}
          loading={isLoading}
          to="/skip/jobs"
        />
        <KpiCard
          label="Open Enquiries"
          value={openEnquiryTotal}
          icon={Inbox}
          loading={isLoading}
          to="/skip/enquiries"
        />
        <KpiCard
          label="Available Inventory"
          value={availableInventory.data?.pagination.total ?? 0}
          icon={Container}
          loading={isLoading}
          to="/skip/inventory"
        />
        <KpiCard
          label="Jobs This Month"
          value={jobsThisMonth.data?.pagination.total ?? 0}
          icon={ListChecks}
          loading={isLoading}
          to="/skip/jobs"
        />
      </div>

      <div className={styles.tableGrid}>
        <Card className={styles.tableCard}>
          <header className={styles.tableHeader}>
            <h2>Recent Jobs</h2>
            <Link className={styles.viewAll} to="/skip/jobs">
              View all
            </Link>
          </header>
          {recentJobs.isLoading ? (
            <p className={styles.empty}>Loading…</p>
          ) : (recentJobs.data?.jobs.length ?? 0) === 0 ? (
            <p className={styles.empty}>No jobs yet.</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Job #</th>
                  <th>Customer</th>
                  <th>Stock Type</th>
                  <th>Delivery</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentJobs.data?.jobs.map((j: SkipJobSummary) => (
                  <tr key={j.enc_id}>
                    <td>
                      <Link to={`/skip/jobs/${encodeURIComponent(j.enc_id)}`} className={styles.cellLink}>
                        {j.job_number}
                      </Link>
                    </td>
                    <td>{j.customer_name}</td>
                    <td>{j.skip_stock_type_name ?? j.skip_type}</td>
                    <td>{fmtDate(j.delivery_date)}</td>
                    <td>
                      <SkipJobStatusBadge status={j.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card className={styles.tableCard}>
          <header className={styles.tableHeader}>
            <h2>Recent Enquiries</h2>
            <Link className={styles.viewAll} to="/skip/enquiries">
              View all
            </Link>
          </header>
          {recentEnquiries.isLoading ? (
            <p className={styles.empty}>Loading…</p>
          ) : (recentEnquiries.data?.enquiries.length ?? 0) === 0 ? (
            <p className={styles.empty}>No enquiries yet.</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Enquiry #</th>
                  <th>Customer</th>
                  <th>Stock Type</th>
                  <th>Created</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentEnquiries.data?.enquiries.map((e: SkipEnquirySummary) => (
                  <tr key={e.enc_id}>
                    <td>
                      <Link
                        to={`/skip/enquiries/${encodeURIComponent(e.enc_id)}`}
                        className={styles.cellLink}
                      >
                        {e.enquiry_number}
                      </Link>
                    </td>
                    <td>{e.customer_name}</td>
                    <td>{e.skip_stock_type_name ?? e.skip_type ?? '—'}</td>
                    <td>{fmtDate(e.created_at)}</td>
                    <td>
                      <SkipEnquiryStatusBadge status={e.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}
